import { useAuth } from '@/app/utils/auth';
import { validator } from '@/app/validator';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useRef } from 'react';
import { z } from 'zod';
import { Logo } from '@/app/ui/components/logo';
import { Card } from '@/app/ui/components/card';
import { Button } from '@/app/ui/components/button';
import { useTranslations } from 'use-intl';

export const Route = createFileRoute('/(auth)/_layout/register')({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      email: z.string().optional(),
      invitationToken: z.string().optional(),
      organizationName: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const { isLoading, isAuthenticated } = useAuth();
  const search = Route.useSearch();

  if (isLoading) return null;

  if (isAuthenticated) return <Navigate to="/" />;

  // If no invitation token, auto-redirect to Auth0 signup (original behavior)
  if (!search.invitationToken) {
    return <AutoRedirect />;
  }

  return <SignupChoice />;
}

function AutoRedirect() {
  const search = Route.useSearch();
  const isRedirecting = useRef(false);
  const { login } = useAuth();

  if (!isRedirecting.current) {
    isRedirecting.current = true;
    void login({
      screenHint: 'signup',
      loginHint: search.email?.replace(/ /g, '%2B') ?? undefined,
      invitationToken: search.invitationToken ?? undefined,
      appState: {
        organizationName: search.organizationName ?? undefined,
      },
    });
  }

  return null;
}

function SignupChoice() {
  const t = useTranslations();
  const search = Route.useSearch();
  const { login } = useAuth();

  const loginHint = search.email?.replace(/ /g, '%2B') ?? undefined;
  const invitationToken = search.invitationToken ?? undefined;
  const appState = {
    organizationName: search.organizationName ?? undefined,
  };

  return (
    <div className="px-5 -translate-y-5 w-full">
      <Logo className="mb-5 mx-auto" />

      <Card className="max-w-92 lg:p-6 p-5 mx-auto w-full">
        <h1 className="text-center font-semibold text-lg mb-0.5 lg:text-xl">
          {t('pages.register.heading')}
        </h1>

        <p className="text-center text-muted text-sm mb-4">{t('pages.register.subheading')}</p>

        <Button
          size="lg"
          className="w-full"
          onClick={() =>
            login({
              screenHint: 'signup',
              loginHint,
              invitationToken,
              appState,
            })
          }
        >
          {t('pages.register.heading')}
        </Button>

        <div className="flex items-center gap-3 my-4">
          <div className="h-px flex-1 bg-border" />
          <span className="text-muted text-xs">{t('general.or')}</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Button
          size="lg"
          variant="secondary"
          className="w-full"
          onClick={() =>
            login({
              connection: import.meta.env.VITE_AUTH0_SSO_CONNECTION,
              loginHint,
              invitationToken,
              appState,
            })
          }
        >
          {t('pages.login.ssoSignup')}
        </Button>
      </Card>
    </div>
  );
}
